import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  MenuItem,
  InputAdornment,
  Alert
} from "@mui/material";
import { FiEye, FiEyeOff } from "react-icons/fi";
import CloseIcon from "@mui/icons-material/Close";
import { motion } from "framer-motion";
import api from "../../api";
import Swal from "sweetalert2";

const AddStudentModal = ({ open, onClose, onAddStudent }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    class: "",
    status: ""
  });

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      setError("Passwords do not match!");
      return;
    }

    setError("");

    try {
      // Prepare payload for API
      const payload = {
        ...formData,
        password,
        usertype: "user", // default usertype
        status: formData.status === "Active" ? true : false
      };

      const response = await api.post("/users", payload);

      // Add to DataGrid via parent callback
      onAddStudent({
        id: response.data.user.id,
        name: response.data.user.name,
        email: response.data.user.email,
        contact: response.data.user.phone,
        status: response.data.user.status ? "Active" : "Inactive",
        class: formData.class,
        photo: response.data.user.profile_image
          ? `${api.defaults.baseURL.replace("/api", "")}/storage/profile_images/${response.data.user.profile_image}`
          : "https://ui-avatars.com/api/?name=Student&background=ddd",
        last_active: response.data.user.last_active || "—"
      });

      Swal.fire({
        icon: "success",
        title: "Added!",
        text: "Student added successfully",
        timer: 1500,
        showConfirmButton: false
      });

      onClose();
      setFormData({ name: "", email: "", phone: "", class: "", status: "" });
      setPassword("");
      setConfirmPassword("");
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: err.response?.data?.message || "Something went wrong",
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ m: 0, p: 2, display: "flex", justifyContent: "space-between" }}>
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          style={{ fontWeight: "bold", fontSize: "1.4rem", color: "#1976d2", display: "flex", gap: "8px" }}
        >
          👨‍🎓 Add New Student
        </motion.div>
        <IconButton aria-label="close" onClick={onClose} sx={{ position: "absolute", top: 8, right: 8, color: "red" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        component={motion.div}
        sx={{ maxHeight: "70vh", overflowY: "auto" }}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <TextField fullWidth label="Full Name" name="name" value={formData.name} onChange={handleChange} margin="dense" required />
        <TextField fullWidth label="Email" name="email" type="email" value={formData.email} onChange={handleChange} margin="dense" required />
        <TextField fullWidth label="Mobile Number" name="phone" type="tel" value={formData.phone} onChange={handleChange} margin="dense" required inputProps={{ pattern: "[0-9]{10}", title: "Enter 10 digit number" }} />
        <TextField fullWidth label="Password" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} margin="dense" required InputProps={{ endAdornment: (<InputAdornment position="end" onClick={() => setShowPassword(!showPassword)}>{showPassword ? <FiEyeOff cursor="pointer" /> : <FiEye cursor="pointer" />}</InputAdornment>) }} />
        <TextField fullWidth label="Confirm Password" type={showConfirmPassword ? "text" : "password"} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} margin="dense" required InputProps={{ endAdornment: (<InputAdornment position="end" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>{showConfirmPassword ? <FiEyeOff cursor="pointer" /> : <FiEye cursor="pointer" />}</InputAdornment>) }} />
        <TextField fullWidth label="Status" name="status" value={formData.status} onChange={handleChange} margin="dense" select required>
          <MenuItem value="Active">Active</MenuItem>
          <MenuItem value="Inactive">Inactive</MenuItem>
        </TextField>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="error" variant="outlined">Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" color="primary" disabled={!formData.name || !formData.email || !formData.phone || !formData.status || !password || !confirmPassword}>Add Student</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddStudentModal;
